/**
 * @caminho_do_arquivo /GoldenSisters_Project/GOLDENSISTERS_ADMIN_WEB/src/pages/ImportProductsPage.js
 * @versao 1.1 - CLEAN: Remoção de dependências não utilizadas
 * @data 11/12/2025
 * @autor Gemini
 */
// [GS-CLEAN] Removido useCallback não utilizado
import React, { useState } from 'react';
import { UploadCloud, FileText, Download, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';

const ImportProductsPage = ({ openModal }) => {
    const [file, setFile] = useState(null);
    const [status, setStatus] = useState('idle'); // idle, processing, success, error
    const [feedback, setFeedback] = useState(null);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile && selectedFile.type === 'text/csv') {
            setFile(selectedFile);
            setStatus('idle');
            setFeedback(null);
        } else {
            setFile(null);
            openModal(<p>Por favor, selecione um arquivo válido no formato .csv.</p>, "Formato Inválido");
        }
    };

    const handleUpload = async () => {
        if (!file) {
            openModal(<p>Nenhum arquivo selecionado.</p>, "Atenção");
            return;
        }

        setStatus('processing');
        setFeedback('Enviando e processando o arquivo... Isso pode levar alguns minutos.');

        setTimeout(() => {
            // Exemplo de simulação de sucesso
            setStatus('success');
            setFeedback({
                message: 'Importação concluída!',
                details: 'Foram processadas 150 linhas. 148 produtos foram criados/atualizados com sucesso e 2 linhas apresentaram erros.',
                errors: [
                    'Linha 15: SKU "ABC-123" já existe com um fornecedor diferente.',
                    'Linha 78: O campo "final_price" está vazio.'
                ]
            });
        }, 3000);
    };

    const handleDownloadTemplate = () => {
        const csvHeader = "sku,product_name,supplier_product_name,supplier_product_code,supplier_id,category_code,description_short,description_long,cost_price,additional_cost,final_price,initial_stock,type,height_cm,length_cm,width_cm,weight_g\n";
        const csvExample = 'COL001012,Colar Lua Minguante,"Colar Prata Lua Ref 88B",LUA-88B,fH7jK9pL3mN5oPqR,COL,"A elegância da noite em seu pescoço.","Colar de prata 925...",45.00,3.50,119.90,10,Venda,5,12,12,95';
        const csvContent = csvHeader + csvExample;

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', 'modelo_importacao.csv');
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    return (
        <div className="p-6 lg:p-8 bg-gray-900 text-white min-h-screen">
            <div className="max-w-4xl mx-auto">
                <h1 className="text-3xl font-bold text-brand-gold mb-4">Importar Produtos em Lote</h1>
                <p className="text-gray-400 mb-8">
                    Faça o upload de múltiplos produtos de uma só vez utilizando uma planilha no formato CSV. Siga o modelo à risca para garantir que todos os seus produtos sejam cadastrados corretamente.
                </p>

                {/* Passo 1: Download do Modelo */}
                <div className="bg-brand-dark p-6 rounded-lg mb-8 border border-gray-700">
                    <h2 className="text-xl font-semibold mb-3 flex items-center"><FileText className="mr-3 text-brand-gold"/>Passo 1: Baixar o Modelo</h2>
                    <p className="text-gray-400 mb-4">
                        Comece baixando a planilha modelo. Ela contém todas as colunas necessárias e no formato correto. Preencha-a com os dados dos seus produtos.
                    </p>
                    <button
                        onClick={handleDownloadTemplate}
                        className="inline-flex items-center px-4 py-2 bg-gray-700 text-white font-semibold rounded-lg hover:bg-gray-600 transition-colors"
                    >
                        <Download className="mr-2" size={18} />
                        Baixar modelo_importacao.csv
                    </button>
                </div>

                {/* Passo 2: Upload do Arquivo */}
                <div className="bg-brand-dark p-6 rounded-lg border border-gray-700">
                    <h2 className="text-xl font-semibold mb-3 flex items-center"><UploadCloud className="mr-3 text-brand-gold"/>Passo 2: Fazer o Upload</h2>
                    <div className="mt-4 border-2 border-dashed border-gray-600 rounded-lg p-8 text-center">
                        <input
                            type="file"
                            id="csv-upload"
                            className="hidden"
                            accept=".csv"
                            onChange={handleFileChange}
                        />
                        <label htmlFor="csv-upload" className="cursor-pointer flex flex-col items-center text-gray-400">
                            <UploadCloud size={48} className="mb-4 text-gray-500" />
                            {file ? (
                                <span className="font-semibold text-green-400">{file.name}</span>
                            ) : (
                                <>
                                    <span className="font-semibold text-brand-gold">Clique para selecionar o arquivo</span>
                                    <span>ou arraste e solte aqui</span>
                                </>
                            )}
                            <span className="text-xs mt-2">Apenas arquivos .csv</span>
                        </label>
                    </div>
                    {file && (
                        <div className="text-center mt-6">
                            <button
                                onClick={handleUpload}
                                disabled={status === 'processing'}
                                className="inline-flex items-center px-8 py-3 bg-green-600 text-white font-bold rounded-lg hover:bg-green-500 transition-colors disabled:bg-gray-500 disabled:cursor-not-allowed"
                            >
                                {status === 'processing' ? (
                                    <Loader2 className="animate-spin mr-2" />
                                ) : (
                                    <UploadCloud className="mr-2" />
                                )}
                                Iniciar Importação
                            </button>
                        </div>
                    )}
                </div>

                {/* Passo 3: Feedback */}
                {feedback && (
                    <div className={`mt-8 p-6 rounded-lg border ${status === 'success' ? 'bg-green-900/20 border-green-500' : status === 'error' ? 'bg-red-900/20 border-red-500' : 'bg-blue-900/20 border-blue-500'}`}>
                        <h2 className="text-xl font-semibold mb-3 flex items-center">
                            {status === 'success' && <CheckCircle className="mr-3 text-green-400"/>}
                            {status === 'error' && <AlertTriangle className="mr-3 text-red-400"/>}
                            {status === 'processing' && <Loader2 className="animate-spin mr-3 text-blue-400"/>}
                            Status da Importação
                        </h2>
                        <p className="font-bold text-lg">{feedback.message}</p>
                        <p className="text-gray-300">{feedback.details}</p>
                        {feedback.errors && feedback.errors.length > 0 && (
                            <div className="mt-4 bg-gray-900/50 p-4 rounded-md">
                                <h3 className="font-semibold mb-2 text-red-400">Detalhes dos Erros:</h3>
                                <ul className="list-disc list-inside text-sm text-gray-400 space-y-1">
                                    {feedback.errors.map((error, index) => (
                                        <li key={index}>{error}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ImportProductsPage;